// "Document expiry" — every paper that runs out within 90 days across the
// people on the list, worst first, grouped the way the mock-up draws it.
// Clicking a row opens that person on the Documents tab.
import React, { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { AxAvatar, AxDrawer } from "../../adminsitration/ui";
import { sendRenewalReminder } from "../../../services/EmployeeApiServices";
import { RANK, docStatus, fmtDate } from "./employeeModel";

const GROUPS = [
  { k: "expired", l: "Already expired — fix today" },
  { k: "critical", l: "Within 15 days" },
  { k: "warning", l: "Within 30 days" },
  { k: "watch", l: "Within 90 days" },
];

const ClockIcon = () => (
  <svg viewBox="0 0 18 18" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.6">
    <circle cx="9" cy="9" r="6.8" />
    <path d="M9 5.2V9l2.7 1.8" />
  </svg>
);

export const expiryRows = (employees, lk) => {
  const rows = [];
  (employees || []).forEach((e) => {
    if (e.status === "Left") return;
    (e.docs || []).forEach((d) => {
      const s = docStatus(d, lk);
      if (RANK[s.k] > 3) return;
      rows.push({ e, d, s });
    });
  });
  return rows.sort((a, b) =>
    RANK[a.s.k] !== RANK[b.s.k]
      ? RANK[a.s.k] - RANK[b.s.k]
      : (a.s.days === null ? 1e9 : a.s.days) - (b.s.days === null ? 1e9 : b.s.days)
  );
};

const csvCell = (x) => '"' + String(x == null ? "" : x).replace(/"/g, '""') + '"';

const EmployeeExpiryDrawer = ({ open, onClose, employees, lookups: lk, branchName, onOpenEmployee }) => {
  const [sending, setSending] = useState(false);
  const rows = useMemo(() => (open ? expiryRows(employees, lk) : []), [open, employees, lk]);
  const expired = rows.filter((r) => r.s.k === "expired").length;

  const exportCsv = () => {
    const lines = [["Employee", "Code", "Branch", "Document", "Number", "Expires", "Status", "Renewal"].map(csvCell).join(",")];
    rows.forEach((r) =>
      lines.push(
        [r.e.name, r.e.code, branchName(r.e.branch), lk.docBy[r.d.type]?.name || r.d.type, r.d.no, r.d.expiry, r.s.lbl, r.d.stage]
          .map(csvCell)
          .join(",")
      )
    );
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" }));
    a.download = "employee-document-expiry.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const remind = async () => {
    const guids = [...new Set(rows.map((r) => r.e.guid))];
    if (!guids.length) return;
    setSending(true);
    try {
      await sendRenewalReminder({ Guids: guids });
      toast.success("Reminders sent to HR, the PRO and each employee");
      onClose();
    } catch (error) {
      console.error("Reminder failed:", error);
      toast.error("Could not send the reminders");
    } finally {
      setSending(false);
    }
  };

  return (
    <AxDrawer
      open={open}
      onClose={onClose}
      icon={<ClockIcon />}
      title="Document expiry"
      subtitle={rows.length + " documents due · " + expired + " already expired"}
      footer={
        <>
          <button type="button" className="axa-btn axa-btn-ghost" onClick={exportCsv} disabled={!rows.length}>
            Export register
          </button>
          <button type="button" className="axa-btn axa-btn-primary" onClick={remind} disabled={!rows.length || sending}>
            {sending ? "Sending…" : "Send reminders"}
          </button>
        </>
      }
    >
      <div className="axe-drawer">
        {!rows.length ? (
          <div className="axa-emptyline">Nothing expires in the next 90 days.</div>
        ) : (
          GROUPS.map((g) => {
            const list = rows.filter((r) => r.s.k === g.k);
            if (!list.length) return null;
            return (
              <React.Fragment key={g.k}>
                <div className="axa-secthead">
                  {g.l} <span className="axe-plain">{list.length}</span>
                </div>
                <table className="axe-gtable">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th style={{ width: 150 }}>Document</th>
                      <th style={{ width: 90 }}>Expires</th>
                      <th style={{ width: 118 }}>Left</th>
                      <th style={{ width: 130 }}>Renewal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((r) => (
                      <tr
                        key={r.e.guid + r.d.type + (r.d.guid || r.d.no)}
                        className="axe-click"
                        onClick={() => onOpenEmployee(r.e)}
                      >
                        <td>
                          <span className="axe-who">
                            <AxAvatar name={r.e.name} />
                            <span>
                              <b>{r.e.name}</b>
                              <i>
                                {branchName(r.e.branch)} · {r.e.desig}
                              </i>
                            </span>
                          </span>
                        </td>
                        <td>{lk.docBy[r.d.type]?.name || r.d.type}</td>
                        <td className="mono">{fmtDate(r.d.expiry)}</td>
                        <td>
                          <span className={"axe-st " + r.s.k}>{r.s.lbl}</span>
                        </td>
                        <td>
                          <span className="axe-st stage">{r.d.stage}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </React.Fragment>
            );
          })
        )}
      </div>
    </AxDrawer>
  );
};

export default EmployeeExpiryDrawer;
